import React, {Fragment, useCallback} from 'react';
import {Alert, Button, Spin} from "antd";
import ExecutableModal from "../../../executableModal/ExecutableModal";
import {ExecutableModalProps} from "../../../../models/Modals";
import DepartmentsDataStore from "../../../../stores/pages/DepartmentsDataStore";
import {DepartmentDataService} from "../../../../models/DepartmentData/DepartmentDataService";
import {ServiceCatalogEntry} from "../../../../models/DepartmentData/ServiceCatalogEntry";
import {useDepartmentDataServicesModal} from "./useDepartmentDataServicesModal";
import {
  DepartmentDataServiceFormValues,
  DepartmentDataServiceUpdateEvent
} from "../../../forms/DepartmentDataServiceForm/DepartmentDataServiceForm.types";
import DepartmentDataServiceForm from "../../../forms/DepartmentDataServiceForm/DepartmentDataServiceForm";
import ActionButton from "../../../declarants/groupButton";
import ee from "../../../../services/EventEmitter";
import {CHANGE_CUSTOM_GROUP_FIELD} from "../../../../constants/events";
import {addMessage, callMessages} from "../../../../helpers/flashMessage";
import {DEPARTMENT_DATA_SERVICES} from "../../../../constants/translates";

export type DepartmentDataServicesModalProps = ExecutableModalProps<{
  groupId: string;
  store: DepartmentsDataStore;
  initialServices?: DepartmentDataService[];
}>;

const DepartmentDataServicesModal = ({ handler, onShow, groupId, ...rest }: DepartmentDataServicesModalProps) => {
  const {
    configuredServices,
    servicesList,
    isLoadingServices,
    serviceLoadError,
    getSavedFormValuesForRow,
    getSavedServiceForRow,
    resetConfiguredServices,
    handleServiceUpdate,
    handleServiceDelete,
    addService,
    loadServices,
  } = useDepartmentDataServicesModal(rest);

  const onSubmit = useCallback((): void => {
    const services: DepartmentDataServiceFormValues[] = [];

    for (const service of configuredServices) {
      if (
        service.valid !== true
        || typeof service.service_id !== 'string'
        || typeof service.amount !== 'string'
      ) {
        addMessage(DEPARTMENT_DATA_SERVICES.incompleteFormError, 'error');
        callMessages();
        return;
      }

      services.push({
        service_id: service.service_id,
        amount: service.amount,
      });
    }

    ee.emit(CHANGE_CUSTOM_GROUP_FIELD, {
      entityId: groupId,
      entityKey: "services",
      value: services,
    });
    handler.executeClose();
  }, [configuredServices, groupId, handler]);

  return (
    <ExecutableModal
      handler={handler}
      showBeforeOnShow
      onHide={resetConfiguredServices}
      onShow={async () => {
        resetConfiguredServices();
        const servicesRequest = loadServices();
        if (onShow) {
          await onShow();
        }
        await servicesRequest;
      }}
      title={DEPARTMENT_DATA_SERVICES.title}
      footer={[
        <Button key="cancel" onClick={handler.executeClose}>
          {DEPARTMENT_DATA_SERVICES.cancel}
        </Button>,
        <Button
          key="save"
          type="primary"
          disabled={isLoadingServices || serviceLoadError}
          onClick={onSubmit}
        >
          {DEPARTMENT_DATA_SERVICES.save}
        </Button>,
      ]}
    >
      <Spin spinning={isLoadingServices} tip={DEPARTMENT_DATA_SERVICES.loading}>
        <div aria-busy={isLoadingServices}>
          <div className={'accent-container column-container'} style={{ paddingTop: 0 }}>
            <ActionButton
              disabled={isLoadingServices || serviceLoadError || servicesList.length === 0}
              onClick={() => addService()}
              size="large"
              text={DEPARTMENT_DATA_SERVICES.add}
            />
          </div>
          { configuredServices.map((service: DepartmentDataServiceUpdateEvent, index: number) => {
            const formId: string = service.formId || `service-mock-id-${index}`;
            const selectedServiceIds: string[] = configuredServices
              .filter(
                (configuredService: DepartmentDataServiceUpdateEvent): boolean =>
                  configuredService.formId !== formId
              )
              .map(
                (configuredService: DepartmentDataServiceUpdateEvent): string | undefined =>
                  configuredService.service_id
              )
              .filter(
                (serviceId: string | undefined): serviceId is string =>
                  typeof serviceId === 'string'
              );
            const savedServiceForRow: DepartmentDataService | undefined = service.formId
              ? getSavedServiceForRow(service.formId)
              : undefined;
            const savedArchivedService: Pick<DepartmentDataService, 'service_id' | 'name'> | undefined =
              savedServiceForRow
              && !servicesList.some(
                (catalogService: ServiceCatalogEntry): boolean =>
                  catalogService.id === savedServiceForRow.service_id
              )
              ? {
                  service_id: savedServiceForRow.service_id,
                  name: savedServiceForRow.name,
                }
              : undefined;

            return (
              <Fragment key={formId}>
                <DepartmentDataServiceForm
                  formId={formId}
                  onUpdate={handleServiceUpdate}
                  onDelete={handleServiceDelete}
                  serviceList={servicesList}
                  selectedServiceIds={selectedServiceIds}
                  savedArchivedService={savedArchivedService}
                  disabled={isLoadingServices || serviceLoadError}
                  initialValues={service.formId ? getSavedFormValuesForRow(service.formId) : undefined}
                />
              </Fragment>
            )
          }) }
        </div>
      </Spin>
      {serviceLoadError && (
        <Alert
          type="error"
          showIcon
          message={DEPARTMENT_DATA_SERVICES.loadError}
          action={
            <Button size="small" onClick={() => void loadServices()}>
              Retry
            </Button>
          }
        />
      )}
    </ExecutableModal>
  );
};

export default DepartmentDataServicesModal;